/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    EyeIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    UserGroupIcon,
    UserIcon,
    ClipboardDocumentListIcon,
    XMarkIcon,
    CreditCardIcon,
    ChevronDownIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import { Listbox, Transition } from '@headlessui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import TabNavigation from '../components/TabNavigation';
import clsx from 'clsx';
import apiService, {
    BillingCycle,
    BillingTransaction,
    Group,
    MemberBilling,
    MemberNote,
    WorkoutProgram
} from '../services/api';

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  membership_type: string;
  membership_type_id?: number;
  membership_type_name?: string;
  membership_type_price?: number;
  membership_type_class_limit_per_week?: number | null;
  membership_type_class_limit_per_month?: number | null;
  avatar_url?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  billing_status?: string;
  next_billing_date?: string;
  billing_amount?: number;
  billing_cycle_name?: string;
  notes_count?: number;
}

const MembersPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { gym } = useAuth();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [memberDetails, setMemberDetails] = useState<{
    member: Member;
    billing: MemberBilling | null;
    transactions: BillingTransaction[];
    notes: MemberNote[];
  } | null>(null);
  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  
  // Program assignment states
  const [memberPrograms, setMemberPrograms] = useState<{
    active_programs: Array<{
      id: number;
      program_id: number;
      program_name: string;
      start_date: string;
      end_date?: string;
      status: 'active' | 'completed' | 'paused' | 'cancelled';
      progress_percentage: number;
    }>;
    available_programs: WorkoutProgram[];
  } | null>(null);
  const [showProgramAssignment, setShowProgramAssignment] = useState(false);
  const [showProgramProgress, setShowProgramProgress] = useState(false);
  const [memberProgress, setMemberProgress] = useState<{
    program_id: number;
    program_name: string;
    total_workouts: number;
    completed_workouts: number;
    progress_percentage: number;
    last_workout_date?: string;
    upcoming_workouts: Array<{
      workout_id: number;
      workout_name: string;
      scheduled_date: string;
      status: 'pending' | 'completed' | 'skipped';
    }>;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<'members' | 'requests' | 'groups' | 'membership-types'>('members');
  const [memberRequests, setMemberRequests] = useState<Array<{
    id: number;
    user_id: number;
    gym_id: number;
    status: 'pending' | 'approved' | 'rejected';
    requested_at: string;
    responded_at?: string;
    responded_by?: number;
    notes?: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
    };
    gym: {
      id: number;
      name: string;
    };
  }>>([]);
  const [requestHistory, setRequestHistory] = useState<Array<{
    id: number;
    user_id: number;
    gym_id: number;
    status: 'approved' | 'rejected';
    requested_at: string;
    responded_at?: string;
    responded_by?: number;
    notes?: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      is_active: boolean;
    };
    gym: {
      id: number;
      name: string;
    };
    responder?: {
      first_name: string;
      last_name: string;
    };
  }>>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  
  // Membership Types states
  const [membershipTypes, setMembershipTypes] = useState<Array<{
    id: number;
    gym_id: number;
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>>([]);
  const [showMembershipTypeModal, setShowMembershipTypeModal] = useState(false);
  const [editingMembershipType, setEditingMembershipType] = useState<{
    id: number;
    gym_id: number;
    name: string;
    description?: string;
    price: number;
    class_limit_per_week?: number | null;
    class_limit_per_month?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  } | null>(null);
  const [membershipTypeForm, setMembershipTypeForm] = useState({
    name: '',
    description: '',
    price: 0,
    class_limit_per_week: null as number | null,
    class_limit_per_month: null as number | null,
    is_active: true
  });
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAssignProgramModal, setShowAssignProgramModal] = useState(false);
  const [availableMembers, setAvailableMembers] = useState<Member[]>([]);
  const [availablePrograms, setAvailablePrograms] = useState<WorkoutProgram[]>([]);
  const [selectedMemberToAdd, setSelectedMemberToAdd] = useState<number | null>(null);
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState<number[]>([]);
  const [selectedProgramToAssign, setSelectedProgramToAssign] = useState<number | null>(null);
  const [selectedProgramsToAssign, setSelectedProgramsToAssign] = useState<number[]>([]);
  const [memberNotes, setMemberNotes] = useState('');
  const [programNotes, setProgramNotes] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [programSearchTerm, setProgramSearchTerm] = useState('');
  const [tempSelectedMembers, setTempSelectedMembers] = useState<number[]>([]);
  const [tempSelectedPrograms, setTempSelectedPrograms] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [showProgramDropdown, setShowProgramDropdown] = useState(false);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: ''
  });
  const [selectedMembersForGroup, setSelectedMembersForGroup] = useState<number[]>([]);

  // Filtered members and programs for search
  const filteredMembers = availableMembers.filter(member => 
    !selectedGroup?.members?.some(gm => gm.user_id === member.id) &&
    (memberSearchTerm === '' || 
     `${member.first_name} ${member.last_name}`.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
     member.email.toLowerCase().includes(memberSearchTerm.toLowerCase()))
  );

  const filteredPrograms = availablePrograms.filter(program => 
    !selectedGroup?.programs?.some(gp => gp.program_id === program.id) &&
    (programSearchTerm === '' || 
     program.name.toLowerCase().includes(programSearchTerm.toLowerCase()) ||
     program.description.toLowerCase().includes(programSearchTerm.toLowerCase()))
  );
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<typeof memberRequests[0] | null>(null);
  const [requestNotes, setRequestNotes] = useState('');
  const [selectedMembershipTypeForRequest, setSelectedMembershipTypeForRequest] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  
  // Form states
  const [newNote, setNewNote] = useState({ note: '', is_private: false });
  const [newTransaction, setNewTransaction] = useState({
    transaction_type: 'one_time' as const,
    amount: 0,
    description: '',
    payment_method: '',
    due_date: '',
    notes: ''
  });
  const [billingData, setBillingData] = useState({
    billing_cycle_id: 0,
    start_date: '',
    amount: 0,
    payment_method: '',
    billing_notes: ''
  });
  
  usePageTitle('Member Management');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMemberDropdown || showProgramDropdown) {
        const target = event.target as Element;
        if (!target.closest('.dropdown-container')) {
          setShowMemberDropdown(false);
          setShowProgramDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMemberDropdown, showProgramDropdown]);

  const loadMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getMembers({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        status: statusFilter
      });
      setMembers(response.members);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Failed to load members:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load members'
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  const loadBillingCycles = useCallback(async () => {
    try {
      const cycles = await apiService.getBillingCycles();
      setBillingCycles(cycles);
    } catch (error) {
      console.error('Failed to load billing cycles:', error);
    }
  }, []);

  const loadMemberPrograms = useCallback(async (memberId: number) => {
    try {
      const programs = await apiService.getMemberPrograms(memberId);
      setMemberPrograms(programs);
    } catch (error) {
      console.error('Failed to load member programs:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load member programs'
      });
    }
  }, []);

  const loadMemberDetails = useCallback(async (memberId: number) => {
    try {
      setIsLoadingDetails(true);
      const details = await apiService.getMemberDetails(memberId);
      if (details && details.member) {
        setMemberDetails(details);
        setSelectedMember(details.member);
        
        // Also load member programs
        await loadMemberPrograms(memberId);
      } else {
        throw new Error('Invalid member details response');
      }
    } catch (error) {
      console.error('Failed to load member details:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load member details'
      });
      setSelectedMember(null);
      setMemberDetails(null);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [loadMemberPrograms]);

  const handleAddNote = async () => {
    if (!selectedMember || !newNote.note.trim()) return;

    try {
      await apiService.addMemberNote(selectedMember.id, newNote);
      setMessage({
        type: 'success',
        text: 'Note added successfully!'
      });
      setNewNote({ note: '', is_private: false });
      setShowAddNote(false);
      loadMemberDetails(selectedMember.id);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add note'
      });
    }
  };

  const handleAddTransaction = async () => {
    if (!selectedMember || !newTransaction.description.trim()) return;

    try {
      await apiService.createBillingTransaction(selectedMember.id, newTransaction);
      setMessage({
        type: 'success',
        text: 'Transaction created successfully!'
      });
      setNewTransaction({
        transaction_type: 'one_time',
        amount: 0,
        description: '',
        payment_method: '',
        due_date: '',
        notes: ''
      });
      setShowAddTransaction(false);
      loadMemberDetails(selectedMember.id);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to create transaction'
      });
    }
  };

  const handleUpdateBilling = async () => {
    if (!selectedMember || !billingData.billing_cycle_id) return;

    try {
      await apiService.updateMemberBilling(selectedMember.id, billingData);
      setMessage({
        type: 'success',
        text: 'Billing updated successfully!'
      });
      setBillingData({
        billing_cycle_id: 0,
        start_date: '',
        amount: 0,
        payment_method: '',
        billing_notes: ''
      });
      setShowBillingModal(false);
      loadMemberDetails(selectedMember.id);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update billing'
      });
    }
  };

  // const handleToggleMemberStatus = async (memberId: number, currentStatus: boolean) => {
  //   try {
  //     await apiService.updateMemberStatus(memberId, !currentStatus);
  //     setMessage({
  //       type: 'success',
  //       text: `Member ${!currentStatus ? 'activated' : 'deactivated'} successfully!`
  //     });
  //     loadMembers();
  //     if (selectedMember?.id === memberId) {
  //       loadMemberDetails(memberId);
  //     }
  //   } catch (error: any) {
  //     setMessage({
  //       type: 'error',
  //       text: error.response?.data?.error || 'Failed to update member status'
  //     });
  //   }
  // };


  const handleAssignProgram = async (memberId: number, programId: number, startDate?: string) => {
    try {
      await apiService.assignProgramToMember(memberId, {
        program_id: programId,
        start_date: startDate
      });
      setMessage({
        type: 'success',
        text: 'Program assigned successfully!'
      });
      loadMemberPrograms(memberId);
      setShowProgramAssignment(false);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to assign program'
      });
    }
  };

  // const handleUpdateProgramStatus = async (memberProgramId: number, status: 'active' | 'completed' | 'paused' | 'cancelled') => {
  //   try {
  //     await apiService.updateMemberProgramStatus(memberProgramId, status);
  //     setMessage({
  //       type: 'success',
  //       text: 'Program status updated successfully!'
  //     });
  //     if (selectedMember) {
  //       loadMemberPrograms(selectedMember.id);
  //     }
  //   } catch (error: any) {
  //     setMessage({
  //       type: 'error',
  //       text: error.response?.data?.error || 'Failed to update program status'
  //     });
  //   }
  // };

  const handleRemoveProgram = async (memberProgramId: number) => {
    try {
      if (!selectedMember) return;
      await apiService.removeProgramFromMember(selectedMember.id, memberProgramId);
      setMessage({
        type: 'success',
        text: 'Program removed successfully!'
      });
      loadMemberPrograms(selectedMember.id);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to remove program'
      });
    }
  };

  // const loadMemberProgress = async (memberId: number, programId?: number) => {
  //   try {
  //     const progress = await apiService.getMemberProgress(memberId, programId);
  //     setMemberProgress(progress);
  //   } catch (error) {
  //     console.error('Failed to load member progress:', error);
  //     setMessage({
  //       type: 'error',
  //       text: 'Failed to load member progress'
  //     });
  //   }
  // };

  const loadMemberRequests = useCallback(async () => {
    try {
      const requests = await apiService.getMemberRequests();
      setMemberRequests(requests);
    } catch (error) {
      console.error('Failed to load member requests:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load member requests'
      });
    }
  }, []);

  const loadRequestHistory = useCallback(async () => {
    try {
      const history = await apiService.getMemberRequestHistory();
      setRequestHistory(history);
    } catch (error) {
      console.error('Failed to load request history:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load request history'
      });
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const response = await apiService.getGroups(true, true);
      setGroups(response.groups);
    } catch (error) {
      console.error('Failed to load groups:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load groups'
      });
    }
  }, []);

  const loadMembershipTypes = useCallback(async () => {
    try {
      const types = await apiService.getMembershipTypes();
      setMembershipTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      console.error('Failed to load membership types:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load membership types'
      });
      setMembershipTypes([]); // Set to empty array on error
    }
  }, []);

  useEffect(() => {
    loadMembers();
    loadBillingCycles();
    loadMembershipTypes(); // Always load membership types as they're used in multiple places
    if (activeTab === 'requests') {
      loadMemberRequests();
      loadRequestHistory();
    } else if (activeTab === 'groups') {
      loadGroups();
    }
  }, [currentPage, searchTerm, statusFilter, activeTab, loadMembers, loadBillingCycles, loadMemberRequests, loadRequestHistory, loadGroups, loadMembershipTypes]);

  // Check for query parameters to open modals or navigate to tabs
  useEffect(() => {
    const action = searchParams.get('action');
    const memberId = searchParams.get('member');
    
    if (action === 'create') {
      // Navigate to requests tab for member creation
      setActiveTab('requests');
      // Remove the query parameter from URL
      setSearchParams({}, { replace: true });
    }
    
    if (memberId && members.length > 0) {
      // Find and load the member details
      const member = members.find(m => m.id === parseInt(memberId));
      if (member) {
        loadMemberDetails(member.id);
      }
      // Remove the query parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, members, loadMemberDetails]);

  const handleCreateGroup = async () => {
    try {
      if (!groupFormData.name.trim()) {
        setMessage({
          type: 'error',
          text: 'Group name is required'
        });
        return;
      }

      // Create the group first
      const response = await apiService.createGroup(groupFormData);
      const newGroup = response.group;

      // Add selected members to the group
      if (selectedMembersForGroup.length > 0) {
        for (const memberId of selectedMembersForGroup) {
          await apiService.addMemberToGroup(newGroup.id, {
            user_id: memberId,
            notes: 'Added during group creation'
          });
        }
      }

      setMessage({
        type: 'success',
        text: `Group created successfully${selectedMembersForGroup.length > 0 ? ` with ${selectedMembersForGroup.length} member(s)` : ''}!`
      });
      setShowCreateGroupModal(false);
      setGroupFormData({ name: '', description: '' });
      setSelectedMembersForGroup([]);
      loadGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to create group'
      });
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    try {
      await apiService.deleteGroup(groupId);
      setMessage({
        type: 'success',
        text: 'Group deleted successfully!'
      });
      loadGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to delete group'
      });
    }
  };

  const loadAvailableMembers = async () => {
    try {
      const response = await apiService.getMembers({ page: 1, limit: 1000 });
      setAvailableMembers(response.members);
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  const handleAddMemberToGroup = async () => {
    if (!selectedGroup || !selectedMemberToAdd) return;

    try {
      await apiService.addMemberToGroup(selectedGroup.id, {
        user_id: selectedMemberToAdd,
        notes: memberNotes
      });
      setMessage({
        type: 'success',
        text: 'Member added to group successfully!'
      });
      setShowAddMemberModal(false);
      setSelectedMemberToAdd(null);
      setMemberNotes('');
      
      // Update the selected group with the new member
      const addedMember = availableMembers.find(m => m.id === selectedMemberToAdd);
      if (addedMember && selectedGroup) {
        const newMember = {
          id: Date.now(), // Temporary ID
          group_id: selectedGroup.id,
          user_id: addedMember.id,
          added_by: 1, // Will be updated by backend
          joined_at: new Date().toISOString(),
          is_active: true,
          first_name: addedMember.first_name,
          last_name: addedMember.last_name,
          email: addedMember.email,
          added_by_first_name: 'You',
          added_by_last_name: ''
        };
        
        setSelectedGroup({
          ...selectedGroup,
          members: [...(selectedGroup.members || []), newMember],
          member_count: (selectedGroup.member_count || 0) + 1
        });
      }
      
      loadGroups(); // Reload groups to update member counts
    } catch (error) {
      console.error('Failed to add member to group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to add member to group'
      });
    }
  };

  const handleAddMembersToGroup = async () => {
    if (!selectedGroup || tempSelectedMembers.length === 0) return;

    try {
      // Add all selected members
      for (const memberId of tempSelectedMembers) {
        await apiService.addMemberToGroup(selectedGroup.id, {
          user_id: memberId,
          notes: memberNotes
        });
      }
      
      setMessage({
        type: 'success',
        text: `${tempSelectedMembers.length} member${tempSelectedMembers.length !== 1 ? 's' : ''} added to group successfully!`
      });
      
      setShowAddMemberModal(false);
      setSelectedMembersToAdd([]);
      setTempSelectedMembers([]);
      setMemberNotes('');
      setMemberSearchTerm('');
      
      // Reload the specific group to update the modal
      await loadGroups();
      // Force a re-fetch of the specific group details
      try {
        const response = await apiService.getGroup(selectedGroup.id);
        setSelectedGroup(response.group);
        console.log('Updated group after adding members:', response.group);
      } catch (error) {
        console.error('Error fetching updated group:', error);
      }
    } catch (error) {
      console.error('Failed to add members to group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to add members to group'
      });
    }
  };

  const handleAddProgramsToGroup = async () => {
    if (!selectedGroup || tempSelectedPrograms.length === 0) return;

    try {
      // Add all selected programs
      for (const programId of tempSelectedPrograms) {
        await apiService.assignProgramToGroup(selectedGroup.id, {
          program_id: programId,
          notes: programNotes
        });
      }
      
      setMessage({
        type: 'success',
        text: `${tempSelectedPrograms.length} program${tempSelectedPrograms.length !== 1 ? 's' : ''} assigned to group successfully!`
      });
      
      setShowAssignProgramModal(false);
      setSelectedProgramsToAssign([]);
      setTempSelectedPrograms([]);
      setProgramNotes('');
      setProgramSearchTerm('');
      
      // Reload the specific group to update the modal
      await loadGroups();
      // Force a re-fetch of the specific group details
      try {
        const response = await apiService.getGroup(selectedGroup.id);
        setSelectedGroup(response.group);
        console.log('Updated group after adding programs:', response.group);
      } catch (error) {
        console.error('Error fetching updated group:', error);
      }
    } catch (error) {
      console.error('Error assigning programs to group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to assign programs to group'
      });
    }
  };

  const handleRemoveMemberFromGroup = async (groupId: number, userId: number) => {
    try {
      await apiService.removeMemberFromGroup(groupId, userId);
      setMessage({
        type: 'success',
        text: 'Member removed from group successfully!'
      });
      
      // Update the selected group by removing the member
      if (selectedGroup && selectedGroup.id === groupId) {
        setSelectedGroup({
          ...selectedGroup,
          members: selectedGroup.members?.filter(member => member.user_id !== userId) || [],
          member_count: Math.max((selectedGroup.member_count || 1) - 1, 0)
        });
      }
      
      loadGroups(); // Reload groups to update member counts
    } catch (error) {
      console.error('Failed to remove member from group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to remove member from group'
      });
    }
  };

  const loadAvailablePrograms = async () => {
    try {
      const response = await apiService.getWorkoutPrograms();
      setAvailablePrograms(response);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const handleAssignProgramToGroup = async () => {
    if (!selectedGroup || !selectedProgramToAssign) return;

    try {
      await apiService.assignProgramToGroup(selectedGroup.id, {
        program_id: selectedProgramToAssign,
        notes: programNotes
      });
      setMessage({
        type: 'success',
        text: 'Program assigned to group successfully!'
      });
      setShowAssignProgramModal(false);
      setSelectedProgramToAssign(null);
      setProgramNotes('');
      
      // Update the selected group with the new program
      const assignedProgram = availablePrograms.find(p => p.id === selectedProgramToAssign);
      if (assignedProgram && selectedGroup) {
        const newProgram = {
          id: Date.now(), // Temporary ID
          group_id: selectedGroup.id,
          program_id: assignedProgram.id,
          assigned_by: 1, // Will be updated by backend
          assigned_at: new Date().toISOString(),
          is_active: true,
          program_name: assignedProgram.name,
          program_description: assignedProgram.description,
          assigned_by_first_name: 'You',
          assigned_by_last_name: ''
        };
        
        setSelectedGroup({
          ...selectedGroup,
          programs: [...(selectedGroup.programs || []), newProgram],
          program_count: (selectedGroup.program_count || 0) + 1
        });
      }
      
      loadGroups(); // Reload groups to update program counts
    } catch (error) {
      console.error('Failed to assign program to group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to assign program to group'
      });
    }
  };

  const handleRemoveProgramFromGroup = async (groupId: number, programId: number) => {
    try {
      await apiService.removeProgramFromGroup(groupId, programId);
      setMessage({
        type: 'success',
        text: 'Program removed from group successfully!'
      });
      
      // Update the selected group by removing the program
      if (selectedGroup && selectedGroup.id === groupId) {
        setSelectedGroup({
          ...selectedGroup,
          programs: selectedGroup.programs?.filter(program => program.program_id !== programId) || [],
          program_count: Math.max((selectedGroup.program_count || 1) - 1, 0)
        });
      }
      
      loadGroups(); // Reload groups to update program counts
    } catch (error) {
      console.error('Failed to remove program from group:', error);
      setMessage({
        type: 'error',
        text: 'Failed to remove program from group'
      });
    }
  };

  const handleRequestAction = async (requestId: number, status: 'approved' | 'rejected') => {
    try {
      await apiService.updateMemberRequest(
        requestId, 
        status, 
        requestNotes, 
        status === 'approved' ? selectedMembershipTypeForRequest : undefined
      );
      setMessage({
        type: 'success',
        text: `Member request ${status} successfully!`
      });
      setShowRequestModal(false);
      setRequestNotes('');
      setSelectedMembershipTypeForRequest(null);
      setSelectedRequest(null);
      loadMemberRequests();
      loadRequestHistory();
      loadMembers(); // Reload members in case someone was approved
      
      // Notify the layout component to update notification count
      window.dispatchEvent(new CustomEvent('notificationUpdate'));
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || `Failed to ${status} request`
      });
    }
  };

  const openRequestModal = (request: typeof memberRequests[0]) => {
    setSelectedRequest(request);
    setRequestNotes(request.notes || '');
    setSelectedMembershipTypeForRequest(null);
    setShowRequestModal(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      await apiService.deleteMember(memberToDelete.id);
      setMessage({
        type: 'success',
        text: 'Member deleted successfully'
      });
      setShowDeleteModal(false);
      setMemberToDelete(null);
      loadMembers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete member'
      });
    }
  };

  const openDeleteModal = (member: Member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string, currentTheme: 'light' | 'dark' = theme) => {
    const textColor = currentTheme === 'dark' ? '200' : '700';
    const borderColor = currentTheme === 'dark' ? '400' : '500';
    
    const statusConfig = {
      active: { 
        color: `bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-orange-${textColor} border-orange-${borderColor}/30`, 
        text: 'Active' 
      },
      inactive: { 
        color: `bg-red-500/20 text-red-${textColor} border-red-${borderColor}/30`, 
        text: 'Inactive' 
      },
      paused: { 
        color: `bg-yellow-500/20 text-yellow-${textColor} border-yellow-${borderColor}/30`, 
        text: 'Paused' 
      },
      cancelled: { 
        color: `bg-gray-500/20 text-gray-${textColor} border-gray-${borderColor}/30`, 
        text: 'Cancelled' 
      }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return (
      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark'
            ? "bg-gradient-to-r from-white to-gray-300"
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          Member Management
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Manage your gym members, billing, and member notes
        </p>
      </div>

      {message && (
        <div className={clsx(
          "rounded-xl p-4 backdrop-blur-sm border",
          message.type === 'success' 
            ? clsx(
                "bg-gradient-to-r from-orange-500/20 to-blue-500/20 border-orange-400/30",
                theme === 'dark' ? "text-orange-200" : "text-orange-700"
              )
            : clsx(
                "bg-red-500/20 border-red-400/30",
                theme === 'dark' ? "text-red-200" : "text-red-700"
              )
        )}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <TabNavigation
        tabs={[
          {
            id: 'members',
            label: 'Members',
            icon: <UserIcon className="h-5 w-5" />
          },
          {
            id: 'groups',
            label: 'Groups',
            icon: <UserGroupIcon className="h-5 w-5" />
          },
          {
            id: 'requests',
            label: 'Member Requests',
            icon: <ClipboardDocumentListIcon className="h-5 w-5" />,
            badge: memberRequests.filter(r => r.status === 'pending').length
          },
          {
            id: 'membership-types',
            label: 'Membership Types',
            icon: <CreditCardIcon className="h-5 w-5" />
          }
        ]}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as 'members' | 'requests' | 'groups' | 'membership-types')}
      />

      {activeTab === 'members' && (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex justify-between items-center mb-6">
              <h2 className={clsx(
                "text-2xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Members</h2>
              <div className="flex space-x-3">
                <div className="relative">
                  <MagnifyingGlassIcon className={clsx(
                    "h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )} />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={clsx(
                      "pl-10 pr-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                    )}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={clsx(
                    "px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 focus:bg-white"
                  )}
                >
                  <option value="all" className="bg-gray-800">All Members</option>
                  <option value="active" className="bg-gray-800">Active</option>
                  <option value="inactive" className="bg-gray-800">Inactive</option>
                  <option value="billing_active" className="bg-gray-800">Billing Active</option>
                  <option value="billing_paused" className="bg-gray-800">Billing Paused</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className={clsx(
                "min-w-full divide-y transition-colors",
                theme === 'dark' ? "divide-white/10" : "divide-gray-200"
              )}>
                <thead className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                )}>
                  <tr>
                    <th className={clsx(
                      "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>
                      Member
                    </th>
                    <th className={clsx(
                      "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>
                      Status
                    </th>
                    <th className={clsx(
                      "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>
                      Billing
                    </th>
                    <th className={clsx(
                      "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>
                      Last Login
                    </th>
                    <th className={clsx(
                      "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={clsx(
                  "bg-transparent divide-y transition-colors",
                  theme === 'dark' ? "divide-white/10" : "divide-gray-200"
                )}>
                  {members.map((member) => (
                    <tr 
                      key={member.id} 
                      className={clsx(
                        "cursor-pointer transition-all duration-200",
                        theme === 'dark'
                          ? "hover:bg-white/5"
                          : "hover:bg-gray-50",
                        selectedMember?.id === member.id ? 'bg-indigo-500/20' : ''
                      )}
                      onClick={() => loadMemberDetails(member.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {member.avatar_url ? (
                              <img
                                className={clsx(
                                  "h-10 w-10 rounded-full border-2 transition-colors",
                                  theme === 'dark' ? "border-white/20" : "border-gray-200"
                                )}
                                src={member.avatar_url}
                                alt=""
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 border-white/20">
                                <span className="text-sm font-medium text-white">
                                  {member.first_name[0]}{member.last_name[0]}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className={clsx(
                              "text-sm font-medium transition-colors",
                              theme === 'dark' ? "text-white" : "text-gray-900"
                            )}>
                              {member.first_name} {member.last_name}
                            </div>
                            <div className={clsx(
                              "text-sm transition-colors",
                              theme === 'dark' ? "text-gray-300" : "text-gray-600"
                            )}>{member.email}</div>
                            {member.phone && (
                              <div className={clsx(
                                "text-xs transition-colors",
                                theme === 'dark' ? "text-gray-400" : "text-gray-500"
                              )}>{member.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(member.is_active ? 'active' : 'inactive', theme)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={clsx(
                          "text-sm transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {member.billing_cycle_name || 'No billing'}
                        </div>
                        {member.billing_amount && (
                          <div className={clsx(
                            "text-xs transition-colors",
                            theme === 'dark' ? "text-gray-300" : "text-gray-600"
                          )}>
                            {formatCurrency(member.billing_amount)}
                          </div>
                        )}
                        {member.next_billing_date && (
                          <div className={clsx(
                            "text-xs transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>
                            Next: {formatDate(member.next_billing_date)}
                          </div>
                        )}
                      </td>
                      <td className={clsx(
                        "px-6 py-4 whitespace-nowrap text-sm transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>
                        {member.last_login
                          ? formatDate(member.last_login)
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadMemberDetails(member.id);
                            }}
                            className={clsx(
                              "inline-flex items-center px-3 py-1 border text-xs leading-4 font-medium rounded-lg transition-all duration-200 bg-yellow-500/20 border-yellow-400/30 hover:bg-yellow-500/30",
                              theme === 'dark' ? "text-yellow-200" : "text-yellow-700"
                            )}
                          >
                            <PencilIcon className="h-3 w-3 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(member);
                            }}
                            className={clsx(
                              "inline-flex items-center px-3 py-1 border text-xs leading-4 font-medium rounded-lg transition-all duration-200 bg-red-500/20 border-red-400/30 hover:bg-red-500/30",
                              theme === 'dark' ? "text-red-200" : "text-red-700"
                            )}
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={clsx(
                      "px-4 py-2 backdrop-blur-sm border rounded-xl hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white"
                        : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    Previous
                  </button>
                  <span className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-600"
                  )}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={clsx(
                      "px-4 py-2 backdrop-blur-sm border rounded-xl hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white"
                        : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      {activeTab === 'requests' && (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={clsx(
                "text-2xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Member Requests</h2>
              <div className="flex space-x-3">
                <div className="relative">
                  <MagnifyingGlassIcon className={clsx(
                    "h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )} />
                  <input
                    type="text"
                    placeholder="Search requests..."
                    className={clsx(
                      "pl-10 pr-4 py-2 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                    )}
                  />
                </div>
              </div>
            </div>

            {memberRequests.length > 0 ? (
              <div className="overflow-x-auto">
                <table className={clsx(
                  "min-w-full divide-y transition-colors",
                  theme === 'dark' ? "divide-white/10" : "divide-gray-200"
                )}>
                  <thead className={clsx(
                    "transition-colors",
                    theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                  )}>
                    <tr>
                      <th className={clsx(
                        "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        Member
                      </th>
                      <th className={clsx(
                        "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        Requested
                      </th>
                      <th className={clsx(
                        "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        Status
                      </th>
                      <th className={clsx(
                        "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={clsx(
                    "bg-transparent divide-y transition-colors",
                    theme === 'dark' ? "divide-white/10" : "divide-gray-200"
                  )}>
                    {memberRequests.map((request) => (
                      <tr key={request.id} className={clsx(
                        "transition-all duration-200",
                        theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                      )}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className={clsx(
                                "h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 transition-colors",
                                theme === 'dark' ? "border-white/20" : "border-gray-200"
                              )}>
                                <span className="text-sm font-medium text-white">
                                  {request.user.first_name[0]}{request.user.last_name[0]}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className={clsx(
                                "text-sm font-medium transition-colors",
                                theme === 'dark' ? "text-white" : "text-gray-900"
                              )}>
                                {request.user.first_name} {request.user.last_name}
                              </div>
                              <div className={clsx(
                                "text-sm transition-colors",
                                theme === 'dark' ? "text-gray-300" : "text-gray-600"
                              )}>{request.user.email}</div>
                              {request.user.phone && (
                                <div className={clsx(
                                  "text-xs transition-colors",
                                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                                )}>{request.user.phone}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={clsx(
                          "px-6 py-4 whitespace-nowrap text-sm transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )}>
                          {formatDate(request.requested_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(request.status, theme)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openRequestModal(request)}
                            className={clsx(
                              "transition-colors duration-200",
                              theme === 'dark' ? "text-indigo-400 hover:text-indigo-300" : "text-indigo-600 hover:text-indigo-700"
                            )}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <UserGroupIcon className={clsx(
                  "h-16 w-16 mx-auto mb-4 transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )} />
                <h3 className={clsx(
                  "text-xl font-bold mb-2 transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>No Member Requests</h3>
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>No pending member requests at this time</p>
              </div>
            )}

            {/* Request History Section */}
            {requestHistory.length > 0 && (
              <div className="mt-8">
                <h3 className={clsx(
                  "text-xl font-bold mb-4 transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>Request History</h3>
                <div className="overflow-x-auto">
                  <table className={clsx(
                    "min-w-full divide-y transition-colors",
                    theme === 'dark' ? "divide-white/10" : "divide-gray-200"
                  )}>
                    <thead className={clsx(
                      "transition-colors",
                      theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                    )}>
                      <tr>
                        <th className={clsx(
                          "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                        )}>
                          Member
                        </th>
                        <th className={clsx(
                          "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                        )}>
                          Requested
                        </th>
                        <th className={clsx(
                          "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                        )}>
                          Request Status
                        </th>
                        <th className={clsx(
                          "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                        )}>
                          Current Status
                        </th>
                        <th className={clsx(
                          "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                        )}>
                          Responded By
                        </th>
                        <th className={clsx(
                          "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                        )}>
                          Responded At
                        </th>
                      </tr>
                    </thead>
                    <tbody className={clsx(
                      "bg-transparent divide-y transition-colors",
                      theme === 'dark' ? "divide-white/10" : "divide-gray-200"
                    )}>
                      {requestHistory.map((request) => (
                        <tr key={request.id} className={clsx(
                          "transition-all duration-200",
                          theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                        )}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className={clsx(
                                  "h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 transition-colors",
                                  theme === 'dark' ? "border-white/20" : "border-gray-200"
                                )}>
                                  <span className="text-sm font-medium text-white">
                                    {request.user.first_name[0]}{request.user.last_name[0]}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className={clsx(
                                  "text-sm font-medium transition-colors",
                                  theme === 'dark' ? "text-white" : "text-gray-900"
                                )}>
                                  {request.user.first_name} {request.user.last_name}
                                </div>
                                <div className={clsx(
                                  "text-sm transition-colors",
                                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                                )}>{request.user.email}</div>
                                {request.user.phone && (
                                  <div className={clsx(
                                    "text-xs transition-colors",
                                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                                  )}>{request.user.phone}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className={clsx(
                            "px-6 py-4 whitespace-nowrap text-sm transition-colors",
                            theme === 'dark' ? "text-gray-300" : "text-gray-600"
                          )}>
                            {formatDate(request.requested_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(request.status, theme)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(request.user.is_active ? 'active' : 'inactive', theme)}
                          </td>
                          <td className={clsx(
                            "px-6 py-4 whitespace-nowrap text-sm transition-colors",
                            theme === 'dark' ? "text-gray-300" : "text-gray-600"
                          )}>
                            {request.responder ? 
                              `${request.responder.first_name} ${request.responder.last_name}` : 
                              'Unknown'
                            }
                          </td>
                          <td className={clsx(
                            "px-6 py-4 whitespace-nowrap text-sm transition-colors",
                            theme === 'dark' ? "text-gray-300" : "text-gray-600"
                          )}>
                            {request.responded_at ? formatDate(request.responded_at) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Member Details Modal */}
      {selectedMember && memberDetails?.member && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors",
              theme === 'dark'
                ? "bg-slate-800/95 border-slate-700/50"
                : "bg-white/95 border-gray-200/50"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "p-6 border-b transition-colors",
              theme === 'dark' ? "border-white/10" : "border-gray-200"
            )}>
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 h-16 w-16">
                    {memberDetails.member.avatar_url ? (
                      <img
                        className="h-16 w-16 rounded-full border-2 border-white/20"
                        src={memberDetails.member.avatar_url}
                        alt=""
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 border-white/20">
                        <span className="text-xl font-medium text-white">
                          {memberDetails.member.first_name?.[0] || ''}{memberDetails.member.last_name?.[0] || ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className={clsx(
                      "text-2xl font-bold transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>
                      {memberDetails.member.first_name || ''} {memberDetails.member.last_name || ''}
                    </h2>
                    <p className={clsx(
                      "transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{memberDetails.member.email || 'No email'}</p>
                    {memberDetails.member.phone && (
                      <p className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>{memberDetails.member.phone}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedMember(null);
                    setMemberDetails(null);
                  }}
                  className={clsx(
                    "transition-colors duration-200",
                    theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                  )}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Member Info */}
                <div className="space-y-4">
                  <h3 className={clsx(
                    "text-lg font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Member Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Status:</span>
                      {getStatusBadge(memberDetails.member.is_active ? 'active' : 'inactive', theme)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Membership Type:</span>
                      <div className="flex items-center space-x-2 relative">
                        <Listbox
                          value={memberDetails.member.membership_type_id || null}
                          onChange={async (newMembershipTypeId) => {
                            try {
                              await apiService.updateMemberMembershipType(memberDetails.member.id, newMembershipTypeId);
                              setMessage({ type: 'success', text: 'Membership type updated successfully' });
                              loadMemberDetails(memberDetails.member.id);
                            } catch (error: any) {
                              setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update membership type' });
                            }
                          }}
                        >
                          {({ open }) => (
                            <>
                              <Listbox.Button
                                className={clsx(
                                  "relative w-full px-3 py-2 text-left backdrop-blur-sm border rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 cursor-pointer",
                                  theme === 'dark'
                                    ? "bg-white/10 border-white/20 text-white hover:bg-white/20 focus:bg-white/20"
                                    : "bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:bg-white"
                                )}
                              >
                                <span className="block truncate pr-6">
                                  {memberDetails.member.membership_type_id
                                    ? (() => {
                                        const selectedType = Array.isArray(membershipTypes) 
                                          ? membershipTypes.find(t => t.id === memberDetails.member.membership_type_id)
                                          : null;
                                        return selectedType 
                                          ? `${selectedType.name} - $${(Number(selectedType.price) || 0).toFixed(2)}/month`
                                          : 'None';
                                      })()
                                    : 'None'}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronDownIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                  />
                                </span>
                              </Listbox.Button>
                              <Transition
                                show={open}
                                as={React.Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <Listbox.Options
                                  className={clsx(
                                    "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border py-1 shadow-lg focus:outline-none",
                                    theme === 'dark'
                                      ? "bg-slate-800 border-slate-700"
                                      : "bg-white border-gray-200"
                                  )}
                                  style={{ top: '100%' }}
                                >
                                  <Listbox.Option
                                    value={null}
                                    className={({ active }) =>
                                      clsx(
                                        "relative cursor-pointer select-none py-2 pl-10 pr-4",
                                        active
                                          ? theme === 'dark'
                                            ? "bg-white/10 text-white"
                                            : "bg-indigo-50 text-indigo-900"
                                          : theme === 'dark'
                                            ? "text-gray-300"
                                            : "text-gray-900"
                                      )
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span className={clsx("block truncate", selected ? "font-medium" : "font-normal")}>
                                          None
                                        </span>
                                        {selected && (
                                          <span className={clsx(
                                            "absolute inset-y-0 left-0 flex items-center pl-3",
                                            theme === 'dark' ? "text-indigo-400" : "text-indigo-600"
                                          )}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Listbox.Option>
                                  {Array.isArray(membershipTypes) && membershipTypes.filter(t => t.is_active).map((type) => (
                                    <Listbox.Option
                                      key={type.id}
                                      value={type.id}
                                      className={({ active }) =>
                                        clsx(
                                          "relative cursor-pointer select-none py-2 pl-10 pr-4",
                                          active
                                            ? theme === 'dark'
                                              ? "bg-white/10 text-white"
                                              : "bg-indigo-50 text-indigo-900"
                                            : theme === 'dark'
                                              ? "text-gray-300"
                                              : "text-gray-900"
                                        )
                                      }
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span className={clsx("block truncate", selected ? "font-medium" : "font-normal")}>
                                            {type.name} - ${(Number(type.price) || 0).toFixed(2)}/month
                                          </span>
                                          {selected && (
                                            <span className={clsx(
                                              "absolute inset-y-0 left-0 flex items-center pl-3",
                                              theme === 'dark' ? "text-indigo-400" : "text-indigo-600"
                                            )}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))}
                                </Listbox.Options>
                              </Transition>
                            </>
                          )}
                        </Listbox>
                      </div>
                    </div>
                    {memberDetails.member.membership_type_name && (
                      <div className="flex justify-between">
                        <span className={clsx(
                          "transition-colors text-sm",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>Plan Details:</span>
                        <span className={clsx(
                          "transition-colors text-sm",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )}>
                          {memberDetails.member.membership_type_class_limit_per_week !== null 
                            ? `${memberDetails.member.membership_type_class_limit_per_week} classes/week`
                            : 'Unlimited'} • {memberDetails.member.membership_type_class_limit_per_month !== null 
                            ? `${memberDetails.member.membership_type_class_limit_per_month} classes/month`
                            : 'Unlimited'}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Email:</span>
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{memberDetails.member.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Phone:</span>
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{memberDetails.member.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Joined:</span>
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{formatDate(memberDetails.member.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Last Login:</span>
                      <span className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>
                        {memberDetails.member.last_login ? formatDate(memberDetails.member.last_login) : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Billing Info */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className={clsx(
                      "text-lg font-semibold transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>Billing Information</h3>
                    <button
                      onClick={() => setShowBillingModal(true)}
                      className={clsx(
                        "text-sm transition-colors",
                        theme === 'dark' ? "text-indigo-400 hover:text-indigo-300" : "text-indigo-600 hover:text-indigo-700"
                      )}
                    >
                      Update Billing
                    </button>
                  </div>
                  {memberDetails.billing ? (
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )}>Billing Cycle:</span>
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>{memberDetails.billing.billing_cycle?.name || 'Unknown'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )}>Amount:</span>
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>{formatCurrency(memberDetails.billing.amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )}>Next Billing:</span>
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>{formatDate(memberDetails.billing.next_billing_date)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className={clsx(
                      "transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>No billing information available</p>
                  )}
                </div>

              </div>

              {/* Notes Section */}
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className={clsx(
                    "text-lg font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Notes</h3>
                  <button
                    onClick={() => setShowAddNote(true)}
                    className="inline-flex items-center px-3 py-2 bg-indigo-500 text-white text-sm rounded-lg hover:bg-indigo-600 transition-colors duration-200"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Note
                  </button>
                </div>
                <div className="space-y-3">
                  {memberDetails.notes.map((note) => (
                    <div key={note.id} className={clsx(
                      "rounded-lg p-4 transition-colors",
                      theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                    )}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={clsx(
                          "text-sm transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>
                          {formatDate(note.created_at)}
                        </span>
                        {note.is_private && (
                          <span className={clsx(
                            "text-xs bg-yellow-500/20 px-2 py-1 rounded",
                            theme === 'dark' ? "text-yellow-200" : "text-yellow-700"
                          )}>
                            Private
                          </span>
                        )}
                      </div>
                      <p className={clsx(
                        "transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{note.note}</p>
                    </div>
                  ))}
                  {memberDetails.notes.length === 0 && (
                    <p className={clsx(
                      "text-center py-4 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>No notes available</p>
                  )}
                </div>
              </div>

              {/* Transactions Section */}
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className={clsx(
                    "text-lg font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Billing Transactions</h3>
                  <button
                    onClick={() => setShowAddTransaction(true)}
                    className="inline-flex items-center px-3 py-2 bg-indigo-500 text-white text-sm rounded-lg hover:bg-indigo-600 transition-colors duration-200"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Transaction
                  </button>
                </div>
                <div className="space-y-3">
                  {memberDetails.transactions.map((transaction) => (
                    <div key={transaction.id} className={clsx(
                      "rounded-lg p-4 transition-colors",
                      theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                    )}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className={clsx(
                            "font-medium transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>{transaction.description}</p>
                          <p className={clsx(
                            "text-sm transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>{transaction.payment_method}</p>
                        </div>
                        <div className="text-right">
                          <p className={clsx(
                            "font-medium transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>{formatCurrency(transaction.amount)}</p>
                          <p className={clsx(
                            "text-sm transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>{formatDate(transaction.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {memberDetails.transactions.length === 0 && (
                    <p className={clsx(
                      "text-center py-4 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>No transactions available</p>
                  )}
                </div>
              </div>

              {/* Programs Section */}
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className={clsx(
                    "text-lg font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Active Programs</h3>
                  <button
                    onClick={() => setShowProgramAssignment(true)}
                    className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-orange-500 to-blue-500 text-white text-sm rounded-lg hover:from-orange-600 hover:to-blue-600 transition-colors duration-200"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Assign Program
                  </button>
                </div>
                <div className="space-y-3">
                  {memberPrograms?.active_programs && memberPrograms.active_programs.length > 0 ? (
                    memberPrograms.active_programs.map((program) => (
                      <div key={program.id} className={clsx(
                        "rounded-lg p-4 transition-colors",
                        theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                      )}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className={clsx(
                              "font-medium transition-colors",
                              theme === 'dark' ? "text-white" : "text-gray-900"
                            )}>{program.program_name}</h4>
                            <p className={clsx(
                              "text-sm transition-colors",
                              theme === 'dark' ? "text-gray-400" : "text-gray-500"
                            )}>
                              Started: {formatDate(program.start_date)}
                              {program.end_date && ` • Ends: ${formatDate(program.end_date)}`}
                            </p>
                            <div className="mt-2">
                              <div className={clsx(
                                "flex justify-between text-sm mb-1 transition-colors",
                                theme === 'dark' ? "text-gray-300" : "text-gray-600"
                              )}>
                                <span>Progress</span>
                                <span>{program.progress_percentage}%</span>
                              </div>
                              <div className={clsx(
                                "w-full rounded-full h-2 transition-colors",
                                theme === 'dark' ? "bg-gray-700" : "bg-gray-200"
                              )}>
                                <div 
                                  className="bg-gradient-to-r from-orange-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${program.progress_percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => {
                                setMemberProgress({ 
                                  program_id: program.program_id, 
                                  program_name: program.program_name, 
                                  total_workouts: 0, 
                                  completed_workouts: 0,
                                  progress_percentage: 0,
                                  upcoming_workouts: []
                                });
                                setShowProgramProgress(true);
                              }}
                              className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                              title="View Progress"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveProgram(program.id)}
                              className="text-red-400 hover:text-red-300 transition-colors duration-200"
                              title="Remove Program"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className={clsx(
                      "text-center py-4 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>No active programs</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showAddNote && selectedMember && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Add Note</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Note
                  </label>
                  <textarea
                    value={newNote.note}
                    onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                    rows={4}
                    placeholder="Enter note..."
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_private"
                    checked={newNote.is_private}
                    onChange={(e) => setNewNote({ ...newNote, is_private: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_private" className="ml-2 text-sm text-gray-300">
                    Private note
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddNote(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200"
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showAddTransaction && selectedMember && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Add Transaction</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newTransaction.description}
                    onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                    placeholder="Enter description..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction({ ...newTransaction, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Payment Method
                  </label>
                  <input
                    type="text"
                    value={newTransaction.payment_method}
                    onChange={(e) => setNewTransaction({ ...newTransaction, payment_method: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                    placeholder="Cash, Card, etc."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddTransaction(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTransaction}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200"
                >
                  Add Transaction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && selectedMember && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Update Billing</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Billing Cycle
                  </label>
                  <select
                    value={billingData.billing_cycle_id}
                    onChange={(e) => setBillingData({ ...billingData, billing_cycle_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                  >
                    <option value={0} className="bg-gray-800">Select billing cycle</option>
                    {billingCycles.map((cycle) => (
                      <option key={cycle.id} value={cycle.id} className="bg-gray-800">
                        {cycle.name} - {formatCurrency(cycle.price)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={billingData.start_date}
                    onChange={(e) => setBillingData({ ...billingData, start_date: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={billingData.amount}
                    onChange={(e) => setBillingData({ ...billingData, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Payment Method
                  </label>
                  <input
                    type="text"
                    value={billingData.payment_method}
                    onChange={(e) => setBillingData({ ...billingData, payment_method: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                    placeholder="Cash, Card, etc."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowBillingModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateBilling}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200"
                >
                  Update Billing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Program Assignment Modal */}
      {showProgramAssignment && selectedMember && memberPrograms && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className={clsx(
                "text-lg font-semibold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Assign Program to {selectedMember.first_name} {selectedMember.last_name}</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Select Program
                  </label>
                  <select
                    onChange={(e) => {
                      const programId = parseInt(e.target.value);
                      if (programId) {
                        handleAssignProgram(selectedMember.id, programId);
                      }
                    }}
                    className="w-full px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20 transition-all duration-200"
                  >
                    <option value="" className="bg-gray-800">Choose a program...</option>
                    {memberPrograms.available_programs.map((program) => (
                      <option key={program.id} value={program.id} className="bg-gray-800">
                        {program.name} - {program.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-sm text-gray-400">
                  <p>Available programs that haven't been assigned to this member yet.</p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowProgramAssignment(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Modal */}
      {showRequestModal && selectedRequest && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border max-w-md w-full transition-colors",
              theme === 'dark'
                ? "bg-slate-800/95 border-slate-700/50"
                : "bg-white/95 border-gray-200/50"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "p-6 border-b transition-colors",
              theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-lg font-semibold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Review Member Request</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {selectedRequest.user.first_name} {selectedRequest.user.last_name}
                  </h4>
                  <p className={clsx(
                    "transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-600"
                  )}>{selectedRequest.user.email}</p>
                  {selectedRequest.user.phone && (
                    <p className={clsx(
                      "transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>{selectedRequest.user.phone}</p>
                  )}
                </div>
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Membership Type (for approval)
                  </label>
                  <select
                    value={selectedMembershipTypeForRequest || ''}
                    onChange={(e) => setSelectedMembershipTypeForRequest(e.target.value ? parseInt(e.target.value) : null)}
                    className={clsx(
                      "w-full px-3 py-2 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50"
                        : "bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    )}
                  >
                    <option value="">Select membership type...</option>
                    {membershipTypes && membershipTypes.filter(t => t.is_active).map((type) => (
                      <option key={type.id} value={type.id} className={theme === 'dark' ? "bg-gray-800" : ""}>
                        {type.name} - ${(type.price || 0).toFixed(2)}/month
                        {type.class_limit_per_week !== null && ` (${type.class_limit_per_week} classes/week)`}
                        {type.class_limit_per_month !== null && ` (${type.class_limit_per_month} classes/month)`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Notes
                  </label>
                  <textarea
                    value={requestNotes}
                    onChange={(e) => setRequestNotes(e.target.value)}
                    className={clsx(
                      "w-full px-3 py-2 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 focus:bg-white/20"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    )}
                    rows={3}
                    placeholder="Add notes about this request..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowRequestModal(false);
                    setSelectedRequest(null);
                    setRequestNotes('');
                    setSelectedMembershipTypeForRequest(null);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRequestAction(selectedRequest.id, 'rejected')}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleRequestAction(selectedRequest.id, 'approved')}
                  disabled={!selectedMembershipTypeForRequest}
                  className={clsx(
                    "px-4 py-2 rounded-lg transition-colors duration-200",
                    selectedMembershipTypeForRequest
                      ? "bg-gradient-to-r from-orange-500 to-blue-500 text-white hover:from-orange-600 hover:to-blue-600"
                      : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  )}
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Groups Tab Content */}
      {activeTab === 'groups' && (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex justify-between items-center mb-6">
            <h2 className={clsx(
              "text-2xl font-bold transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Groups</h2>
            <button
              onClick={() => {
                loadAvailableMembers();
                setShowCreateGroupModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Create Group</span>
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className={clsx(
                "w-16 h-16 mx-auto mb-4 transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )} />
              <h3 className={clsx(
                "text-lg font-semibold mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-700"
              )}>No Groups Yet</h3>
              <p className={clsx(
                "transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>Use the "Create Group" button above to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className={clsx(
                    "backdrop-blur-sm rounded-xl border p-6 transition-all duration-200 relative group",
                    theme === 'dark'
                      ? "bg-white/5 border-white/10 hover:bg-white/10"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: group.color }}
                      ></div>
                      <h3 className={clsx(
                        "text-lg font-semibold transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{group.name}</h3>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroupToDelete(group);
                        setShowDeleteConfirm(true);
                      }}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors duration-200 relative z-10"
                      title="Delete Group"
                    >
                      <svg className={clsx(
                        "w-4 h-4 transition-colors",
                        theme === 'dark' ? "text-red-400" : "text-red-600"
                      )} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  
                  {group.description && (
                    <p className={clsx(
                      "text-sm mb-4 line-clamp-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{group.description}</p>
                  )}
                  
                  <div className={clsx(
                    "flex items-center space-x-6 text-sm transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>
                    <span className="flex items-center space-x-2">
                      <UserGroupIcon className="w-4 h-4" />
                      <span>{group.member_count || 0} members</span>
                    </span>
                    <span className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span>{group.program_count || 0} programs</span>
                    </span>
                  </div>
                  
                  <div 
                    className="absolute inset-0 cursor-pointer"
                    onClick={() => {
                      setSelectedGroup(group);
                      setShowGroupModal(true);
                    }}
                    style={{ clipPath: 'polygon(0 0, calc(100% - 50px) 0, calc(100% - 50px) 100%, 0 100%)' }}
                  ></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Group Detail Modal */}
      {showGroupModal && selectedGroup && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white border-gray-200"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "p-6 border-b transition-colors",
              theme === 'dark' ? "border-white/10" : "border-gray-200"
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-6 h-6 rounded-full"
                    style={{ backgroundColor: selectedGroup.color }}
                  ></div>
                  <h3 className={clsx(
                    "text-xl font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>{selectedGroup.name}</h3>
                </div>
                <button
                  onClick={() => setShowGroupModal(false)}
                  className={clsx(
                    "transition-colors duration-200",
                    theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                  )}
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              {selectedGroup.description && (
                <p className={clsx(
                  "mt-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>{selectedGroup.description}</p>
              )}
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Members Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={clsx(
                      "text-lg font-semibold transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>Members ({selectedGroup.members?.length || 0})</h4>
                    <button
                      onClick={() => {
                        loadAvailableMembers();
                        setTempSelectedMembers([]);
                        setMemberSearchTerm('');
                        setMemberNotes('');
                        setShowAddMemberModal(true);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>Add Member</span>
                    </button>
                  </div>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedGroup.members && selectedGroup.members.length > 0 ? (
                      selectedGroup.members.map((member) => (
                        <div
                          key={member.id}
                          className={clsx(
                            "flex items-center justify-between p-3 rounded-lg transition-colors",
                            theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                          )}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                              {member.first_name[0]}{member.last_name[0]}
                            </div>
                            <div>
                              <p className={clsx(
                                "font-medium transition-colors",
                                theme === 'dark' ? "text-white" : "text-gray-900"
                              )}>{member.first_name} {member.last_name}</p>
                              <p className={clsx(
                                "text-sm transition-colors",
                                theme === 'dark' ? "text-gray-400" : "text-gray-500"
                              )}>{member.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveMemberFromGroup(selectedGroup.id, member.user_id)}
                            className={clsx(
                              "transition-colors duration-200",
                              theme === 'dark' ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-700"
                            )}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className={clsx(
                        "text-center py-4 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>No members in this group</p>
                    )}
                  </div>
                </div>

                {/* Programs Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={clsx(
                      "text-lg font-semibold transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>Programs ({selectedGroup.programs?.length || 0})</h4>
                    <button
                      onClick={() => {
                        loadAvailablePrograms();
                        setTempSelectedPrograms([]);
                        setProgramSearchTerm('');
                        setProgramNotes('');
                        setShowAssignProgramModal(true);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-orange-500 to-blue-500 text-white rounded-lg hover:from-orange-600 hover:to-blue-600 transition-colors duration-200 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>Assign Program</span>
                    </button>
                  </div>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedGroup.programs && selectedGroup.programs.length > 0 ? (
                      selectedGroup.programs.map((program) => (
                        <div
                          key={program.id}
                          className={clsx(
                            "flex items-center justify-between p-3 rounded-lg transition-colors",
                            theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                          )}
                        >
                          <div>
                            <p className={clsx(
                              "font-medium transition-colors",
                              theme === 'dark' ? "text-white" : "text-gray-900"
                            )}>{program.program_name}</p>
                            <p className={clsx(
                              "text-sm transition-colors",
                              theme === 'dark' ? "text-gray-400" : "text-gray-500"
                            )}>{program.program_description}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveProgramFromGroup(selectedGroup.id, program.program_id)}
                            className={clsx(
                              "transition-colors duration-200",
                              theme === 'dark' ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-700"
                            )}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className={clsx(
                        "text-center py-4 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>No programs assigned to this group</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedGroup && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border max-w-2xl w-full max-h-[80vh] overflow-hidden transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white border-gray-200"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "p-6 border-b transition-colors",
              theme === 'dark' ? "border-white/10" : "border-gray-200"
            )}>
              <h3 className={clsx(
                "text-lg font-semibold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Add Members to {selectedGroup.name}</h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Search Bar */}
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Search Members
                </label>
                <input
                  type="text"
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className={clsx(
                    "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  )}
                />
              </div>
              
              {/* Member Selection List */}
              <div className={clsx(
                "max-h-96 overflow-y-auto border rounded-lg transition-colors",
                theme === 'dark' ? "border-white/20" : "border-gray-200"
              )}>
                {filteredMembers.length > 0 ? (
                  <div className="space-y-1 p-2">
                    {filteredMembers.map((member) => (
                      <label key={member.id} className={clsx(
                        "flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors",
                        theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                      )}>
                        <input
                          type="checkbox"
                          checked={tempSelectedMembers.includes(member.id)}
                          onChange={(e) => {
                            console.log('Member checkbox changed:', member.id, e.target.checked);
                            console.log('Current tempSelectedMembers:', tempSelectedMembers);
                            if (e.target.checked) {
                              const newSelection = [...tempSelectedMembers, member.id];
                              console.log('Adding member, new selection:', newSelection);
                              setTempSelectedMembers(newSelection);
                            } else {
                              const newSelection = tempSelectedMembers.filter(id => id !== member.id);
                              console.log('Removing member, new selection:', newSelection);
                              setTempSelectedMembers(newSelection);
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <p className={clsx(
                            "text-sm font-medium transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>{member.first_name} {member.last_name}</p>
                          <p className={clsx(
                            "text-xs transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>{member.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className={clsx(
                    "p-4 text-center transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>
                    {memberSearchTerm ? 'No members found matching your search.' : 'No members available.'}
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Notes (Optional)
                </label>
                <textarea
                  value={memberNotes}
                  onChange={(e) => setMemberNotes(e.target.value)}
                  placeholder="Add any notes about these members..."
                  className={clsx(
                    "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  )}
                  rows={3}
                />
              </div>
            </div>
            
            <div className={clsx(
              "p-6 border-t flex justify-between items-center transition-colors",
              theme === 'dark' ? "border-white/10" : "border-gray-200"
            )}>
              <span className={clsx(
                "text-sm transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>
                {tempSelectedMembers.length} member{tempSelectedMembers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowAddMemberModal(false);
                    setTempSelectedMembers([]);
                    setMemberNotes('');
                    setMemberSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMembersToGroup}
                  disabled={tempSelectedMembers.length === 0}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Program Modal */}
      {showAssignProgramModal && selectedGroup && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white border-gray-200"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "p-6 border-b transition-colors",
              theme === 'dark' ? "border-white/10" : "border-gray-200"
            )}>
              <h3 className={clsx(
                "text-lg font-semibold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Assign Programs to {selectedGroup.name}</h3>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Search Bar */}
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Search Programs
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className={clsx(
                    "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )} />
                  <input
                    type="text"
                    value={programSearchTerm}
                    onChange={(e) => setProgramSearchTerm(e.target.value)}
                    placeholder="Search programs..."
                    className={clsx(
                      "w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    )}
                  />
                </div>
              </div>
              
              {/* Program Selection List */}
              <div className={clsx(
                "max-h-96 overflow-y-auto border rounded-lg transition-colors",
                theme === 'dark' ? "border-white/20" : "border-gray-200"
              )}>
                {filteredPrograms.length > 0 ? (
                  <div className="space-y-1 p-2">
                    {filteredPrograms.map((program) => (
                      <label key={program.id} className={clsx(
                        "flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors",
                        theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                      )}>
                        <input
                          type="checkbox"
                          checked={tempSelectedPrograms.includes(program.id)}
                          onChange={(e) => {
                            console.log('Program checkbox changed:', program.id, e.target.checked);
                            console.log('Current tempSelectedPrograms:', tempSelectedPrograms);
                            if (e.target.checked) {
                              const newSelection = [...tempSelectedPrograms, program.id];
                              console.log('Adding program, new selection:', newSelection);
                              setTempSelectedPrograms(newSelection);
                            } else {
                              const newSelection = tempSelectedPrograms.filter(id => id !== program.id);
                              console.log('Removing program, new selection:', newSelection);
                              setTempSelectedPrograms(newSelection);
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <p className={clsx(
                            "text-sm font-medium transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>{program.name}</p>
                          <p className={clsx(
                            "text-xs transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>{program.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className={clsx(
                      "transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>No programs available</p>
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Notes (Optional)
                </label>
                <textarea
                  value={programNotes}
                  onChange={(e) => setProgramNotes(e.target.value)}
                  className={clsx(
                    "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  )}
                  placeholder="Add notes about this program assignment..."
                  rows={3}
                />
              </div>
            </div>
            
            <div className={clsx(
              "p-6 border-t flex justify-between items-center transition-colors",
              theme === 'dark' ? "border-white/10" : "border-gray-200"
            )}>
              <span className={clsx(
                "text-sm transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>
                {tempSelectedPrograms.length} program{tempSelectedPrograms.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowAssignProgramModal(false);
                    setTempSelectedPrograms([]);
                    setProgramNotes('');
                    setProgramSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddProgramsToGroup}
                  disabled={tempSelectedPrograms.length === 0}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Create New Group</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Group Details */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-white">Group Details</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Group Name *
                    </label>
                    <input
                      type="text"
                      value={groupFormData.name}
                      onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter group name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={groupFormData.description}
                      onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter group description (optional)"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Member Selection */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-white">Add Members (Optional)</h4>
                  <div className="max-h-64 overflow-y-auto border border-white/20 rounded-lg p-3">
                    {availableMembers.length > 0 ? (
                      <div className="space-y-2">
                        {availableMembers.map((member) => (
                          <label key={member.id} className="flex items-center space-x-3 p-2 hover:bg-white/5 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedMembersForGroup.includes(member.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMembersForGroup([...selectedMembersForGroup, member.id]);
                                } else {
                                  setSelectedMembersForGroup(selectedMembersForGroup.filter(id => id !== member.id));
                                }
                              }}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <div className="flex-1">
                              <p className="text-white text-sm font-medium">{member.first_name} {member.last_name}</p>
                              <p className="text-gray-400 text-xs">{member.email}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-4">No members available</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {selectedMembersForGroup.length} member(s) selected
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateGroupModal(false);
                  setGroupFormData({ name: '', description: '' });
                  setSelectedMembersForGroup([]);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200"
              >
                Create Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && memberToDelete && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Delete Member</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-4">
                Are you sure you want to delete {memberToDelete.first_name} {memberToDelete.last_name}? 
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteMember}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteConfirm && groupToDelete && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Delete Group</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-4">
                Are you sure you want to delete the group <span className="font-semibold text-white">"{groupToDelete.name}"</span>? 
                This action cannot be undone and will remove all members and programs from this group.
              </p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setGroupToDelete(null);
                }}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteGroup(groupToDelete.id);
                  setShowDeleteConfirm(false);
                  setGroupToDelete(null);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Membership Types Tab Content */}
      {activeTab === 'membership-types' && (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex justify-between items-center mb-6">
            <h2 className={clsx(
              "text-2xl font-bold transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Membership Types</h2>
            <button
              onClick={() => {
                setEditingMembershipType(null);
                setMembershipTypeForm({
                  name: '',
                  description: '',
                  price: 0,
                  class_limit_per_week: null,
                  class_limit_per_month: null,
                  is_active: true
                });
                setShowMembershipTypeModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-blue-500 text-white rounded-lg hover:from-orange-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Create Membership Type</span>
            </button>
          </div>

          {!Array.isArray(membershipTypes) || membershipTypes.length === 0 ? (
            <div className={clsx(
              "text-center py-12 transition-colors",
              theme === 'dark' ? "text-gray-400" : "text-gray-600"
            )}>
              <CreditCardIcon className={clsx(
                "h-16 w-16 mx-auto mb-4 transition-colors",
                theme === 'dark' ? "text-gray-600" : "text-gray-400"
              )} />
              <p className="text-lg mb-2">No membership types yet</p>
              <p className="text-sm">Create your first membership type to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(membershipTypes) && membershipTypes.map((type) => (
                <div
                  key={type.id}
                  className={clsx(
                    "rounded-xl border p-6 transition-all duration-200 hover:scale-105",
                    theme === 'dark'
                      ? "bg-white/5 border-white/10 hover:bg-white/10"
                      : "bg-white border-gray-200 hover:shadow-lg"
                  )}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className={clsx(
                        "text-xl font-bold mb-1 transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{type.name}</h3>
                      {type.description && (
                        <p className={clsx(
                          "text-sm transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-600"
                        )}>{type.description}</p>
                      )}
                    </div>
                    <span className={clsx(
                      "px-2 py-1 text-xs font-semibold rounded-full",
                      type.is_active
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                    )}>
                      {type.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className={clsx(
                    "text-2xl font-bold mb-4 transition-colors",
                    theme === 'dark' ? "text-orange-400" : "text-orange-600"
                  )}>
                    ${(Number(type.price) || 0).toFixed(2)}<span className={clsx(
                      "text-sm font-normal transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-600"
                    )}>/month</span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className={clsx(
                        "text-sm transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-600"
                      )}>Weekly Limit:</span>
                      <span className={clsx(
                        "text-sm font-semibold transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>
                        {type.class_limit_per_week === null ? 'Unlimited' : `${type.class_limit_per_week} classes`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={clsx(
                        "text-sm transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-600"
                      )}>Monthly Limit:</span>
                      <span className={clsx(
                        "text-sm font-semibold transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>
                        {type.class_limit_per_month === null ? 'Unlimited' : `${type.class_limit_per_month} classes`}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-2 mt-4">
                    <button
                      onClick={() => {
                        setEditingMembershipType(type);
                        setMembershipTypeForm({
                          name: type.name,
                          description: type.description || '',
                          price: type.price,
                          class_limit_per_week: type.class_limit_per_week ?? null,
                          class_limit_per_month: type.class_limit_per_month ?? null,
                          is_active: type.is_active
                        });
                        setShowMembershipTypeModal(true);
                      }}
                      className={clsx(
                        "flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                        theme === 'dark'
                          ? "text-blue-200 hover:bg-blue-500/20"
                          : "text-blue-700 hover:bg-blue-100"
                      )}
                    >
                      <PencilIcon className="w-4 h-4 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Are you sure you want to delete "${type.name}"?`)) {
                          try {
                            await apiService.deleteMembershipType(type.id);
                            setMessage({ type: 'success', text: 'Membership type deleted successfully' });
                            loadMembershipTypes();
                          } catch (error: any) {
                            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete membership type' });
                          }
                        }
                      }}
                      className={clsx(
                        "flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                        theme === 'dark'
                          ? "text-red-200 hover:bg-red-500/20"
                          : "text-red-700 hover:bg-red-100"
                      )}
                    >
                      <TrashIcon className="w-4 h-4 inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Membership Type Create/Edit Modal */}
      {showMembershipTypeModal && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMembershipTypeModal(false);
            }
          }}
        >
          <div 
            className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors",
              theme === 'dark'
                ? "bg-slate-800/95 border-slate-700/50"
                : "bg-white/95 border-gray-200/50"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "p-6 border-b transition-colors flex items-center justify-between",
              theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>
                {editingMembershipType ? 'Edit Membership Type' : 'Create Membership Type'}
              </h3>
              <button
                onClick={() => setShowMembershipTypeModal(false)}
                className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                )}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Name *
                </label>
                <input
                  type="text"
                  value={membershipTypeForm.name}
                  onChange={(e) => setMembershipTypeForm({ ...membershipTypeForm, name: e.target.value })}
                  className={clsx(
                    "w-full px-4 py-2 rounded-lg border transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  )}
                  placeholder="e.g., Basic, Premium, Unlimited"
                />
              </div>

              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Description
                </label>
                <textarea
                  value={membershipTypeForm.description}
                  onChange={(e) => setMembershipTypeForm({ ...membershipTypeForm, description: e.target.value })}
                  rows={3}
                  className={clsx(
                    "w-full px-4 py-2 rounded-lg border transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  )}
                  placeholder="Describe this membership type..."
                />
              </div>

              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Monthly Price ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={membershipTypeForm.price}
                  onChange={(e) => setMembershipTypeForm({ ...membershipTypeForm, price: parseFloat(e.target.value) || 0 })}
                  className={clsx(
                    "w-full px-4 py-2 rounded-lg border transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  )}
                  placeholder="0.00"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Weekly Class Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={membershipTypeForm.class_limit_per_week === null ? '' : membershipTypeForm.class_limit_per_week}
                    onChange={(e) => setMembershipTypeForm({ 
                      ...membershipTypeForm, 
                      class_limit_per_week: e.target.value === '' ? null : parseInt(e.target.value) || null 
                    })}
                    className={clsx(
                      "w-full px-4 py-2 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    )}
                    placeholder="Unlimited"
                  />
                  <p className={clsx(
                    "text-xs mt-1 transition-colors",
                    theme === 'dark' ? "text-gray-500" : "text-gray-500"
                  )}>Leave empty for unlimited</p>
                </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Monthly Class Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={membershipTypeForm.class_limit_per_month === null ? '' : membershipTypeForm.class_limit_per_month}
                    onChange={(e) => setMembershipTypeForm({ 
                      ...membershipTypeForm, 
                      class_limit_per_month: e.target.value === '' ? null : parseInt(e.target.value) || null 
                    })}
                    className={clsx(
                      "w-full px-4 py-2 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    )}
                    placeholder="Unlimited"
                  />
                  <p className={clsx(
                    "text-xs mt-1 transition-colors",
                    theme === 'dark' ? "text-gray-500" : "text-gray-500"
                  )}>Leave empty for unlimited</p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={membershipTypeForm.is_active}
                  onChange={(e) => setMembershipTypeForm({ ...membershipTypeForm, is_active: e.target.checked })}
                  className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className={clsx(
                  "ml-2 text-sm transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Active (available for assignment)
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowMembershipTypeModal(false)}
                  className={clsx(
                    "px-4 py-2 rounded-lg transition-colors",
                    theme === 'dark'
                      ? "bg-gray-700 text-white hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  )}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!membershipTypeForm.name || membershipTypeForm.price < 0) {
                      setMessage({ type: 'error', text: 'Please fill in all required fields' });
                      return;
                    }

                    try {
                      if (editingMembershipType) {
                        await apiService.updateMembershipType(editingMembershipType.id, membershipTypeForm);
                        setMessage({ type: 'success', text: 'Membership type updated successfully' });
                      } else {
                        await apiService.createMembershipType(membershipTypeForm);
                        setMessage({ type: 'success', text: 'Membership type created successfully' });
                      }
                      setShowMembershipTypeModal(false);
                      loadMembershipTypes();
                    } catch (error: any) {
                      setMessage({ 
                        type: 'error', 
                        text: error.response?.data?.error || `Failed to ${editingMembershipType ? 'update' : 'create'} membership type` 
                      });
                    }
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-blue-500 text-white rounded-lg hover:from-orange-600 hover:to-blue-600 transition-all duration-200"
                >
                  {editingMembershipType ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPage;
